<template>
  <q-card-section class="q-pt-none">
    <div class="row q-gutter-sm q-my-sm">
      <div class="col">
        <q-input
          :model-value="
            useFormatDate(start).time({
              hour: 'numeric',
              minute: 'numeric',
              hourCycle: 'h23',
            })
          "
          @update:model-value="(val) => (isValidDate(val) ? updateStart(toDate(val, date) as Date) : undefined)"
          ref="startRef"
          reactive-rules
          :rules="[
            (val) => isValidDate(val) || '',
            (val) => rules.beforeNow(toDate(val, date)) || formLabel('notBeforeNow'),
            (val) => rules.beforeEnd(toDate(val, date)) || formLabel('startOverlapEnd'),
            (val) =>
              rules.notBetweenTimers(toDate(val, date), index) || formLabel('betweenActivities'),
            (val) =>
              rules.notOverlap(toDate(val, date), 'start', index) || formLabel('overlapActivities'),
          ]"
          color="accent"
          filled
          type="time"
          :label="formLabel('startTime')"
          :disable="traveling"
        />
      </div>
      <div class="col">
        <q-input
          v-model.number="startOdo"
          ref="startOdoRef"
          type="number"
          filled
          color="accent"
          :label="formLabel('startOdo')"
          reactive-rules
          :rules="[
            (val) => (endOdo ? odoRules.beforeEnd(val) : true || formLabel('odometerLarger')),
            (val) => odoRules.noDecimal(val) || 'No decimals allowed'
          ]"
          :disable="traveling"
        />
      </div>
    </div>
    <template v-if="initContinue || traveling || insert">
      <div
        class="row q-gutter-sm q-my-sm"
        v-for="(loc, locIndex) in locationInputs"
        :key="loc.name"
      >
        <div class="col">
          <TypeAheadStores
            v-model="loc.ref"
            :disable="traveling"
            :ref="(getLocationRef(locIndex) as any)"
            :label="loc.label"
            :rules="[
              (val: any) =>
                destinationRules.selectionNotNull(loc.ref) || errorMessage('locationNotNull'),
              (val: any) => destinationRules.notBothHome() || errorMessage('departDestNotSame'),
              (val: any) => destinationRules.storesDifferent() || errorMessage('departDestNotSame'),
            ]"
          />
        </div>
      </div>

      <!-- Address -->
      <div
        v-if="
          destination && destination.type === 'store' && destination.address && !edit && !insert
        "
        class="row justify-between q-mt-sm"
      >
        <a
          target="_blank"
          :href="`
          https://www.google.com/maps/place/
          ${destination.address.street.replaceAll(' ', '+')},
          +${destination.address.city},
          +${destination.address.state}
          +${destination.address.zip}`"
        >
          <div>
            {{ destination.address.street }}<br />
            {{ destination.address.city }}, {{ destination.address.state }}
            {{ destination.address.zip }}
          </div>
          <div>
            <q-btn round color="secondary" icon="map" />
          </div>
        </a>
      </div>

      <!-- Traveling -->
      <div v-if="traveling" class="row q-gutter-sm q-my-sm">
        <div class="col">
          <p class="text-weight-bold">{{ formLabel('travelingMessage') }}</p>
        </div>
      </div>
    </template>

    <!-- End travel info -->
    <template v-if="!traveling && (stopped === true || insert)">
      <div class="row q-gutter-sm q-my-sm">
        <div class="col">
          <q-input
            :model-value="
              useFormatDate(end).time({
                hour: 'numeric',
                minute: 'numeric',
                hourCycle: 'h23',
              })
            "
            @update:model-value="(val) => (isValidDate(val) ? updateEnd(toDate(val, date) as Date) : undefined)"
            ref="endRef"
            reactive-rules
            :rules="[
              (val) => isValidDate(val) || '',
              (val) => rules.beforeNow(toDate(val, date)) || formLabel('notBeforeNow'),
              (val) => rules.afterStart(toDate(val, date)) || formLabel('endOverlapStart'),
              (val) =>
                rules.notBetweenTimers(toDate(val, date), index) || formLabel('betweenActivities'),
              (val) =>
                rules.notOverlap(toDate(val, date), 'end', index) || formLabel('overlapActivities'),
            ]"
            color="accent"
            filled
            type="time"
            :label="formLabel('endTime')"
          />
        </div>
        <div class="col">
          <q-input
            v-model.number="endOdo"
            ref="endOdoRef"
            type="number"
            filled
            color="accent"
            :label="formLabel('endOdo')"
            reactive-rules
            :rules="[
              (val) => odoRules.beforeStart(val) || formLabel('odometerLarger'),
              (val) => odoRules.noDecimal(val) || 'No decimals allowed'
              ]"
          />
        </div>
      </div>
      <div class="row q-gutter-sm">
        <div class="col">
          <q-input
            filled
            bg-color="white"
            color="accent"
            v-model="duration"
            readonly
            :label="formLabel('duration')"
          />
        </div>
        <div class="col">
          <q-input
            filled
            bg-color="white"
            color="accent"
            v-model="distance"
            readonly
            :label="formLabel('distance')"
          />
        </div>
      </div>
    </template>
  </q-card-section>

  <q-card-actions align="right" class="text-primary">
    <!-- Start Travel -->
    <template v-if="!traveling && !stopped && !insert">
      <q-btn flat :label="formLabel('close')" @click="close()" v-close-popup />
      <template v-if="!initContinue">
        <q-btn
          color="primary"
          :label="formLabel('continue')"
          @click="initContinue = true"
          v-close-popup
        />
      </template>
      <template v-if="initContinue && destination && departing">
        <q-btn color="primary" :label="formLabel('startTravel')" @click="travel" v-close-popup />
      </template>
    </template>

    <!-- Stop Travel -->
    <template v-if="traveling && !stopped">
      <q-btn
        color="primary"
        :label="formLabel('stopTravel')"
        size="lg"
        class="full-width"
        @click="stop"
        v-close-popup
      />
    </template>

    <!-- End of travel/stopped -->
    <template v-if="!traveling && isIncomplete">
      <q-btn-dropdown color="primary" split @click="saveTravel">
        <template #label> {{ formLabel('saveTravel') }} </template>
        <q-list>
          <q-item clickable v-close-popup @click="removeTravel">
            <q-item-section>
              <q-item-label>{{ formLabel('delete') }}</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </q-btn-dropdown>
    </template>
    <template v-if="!traveling && insert && !isIncomplete">
      <q-item clickable v-close-popup="2" v-if="insert">
        <q-item-section>
          <q-item-label>{{ formLabel('cancel') }}</q-item-label>
        </q-item-section>
      </q-item>
      <q-btn
        color="primary"
        v-if="insert && !index && index != 0"
        split
        @click="insertOrEditTravel"
        :label="formLabel('saveTravel')"
      />
      <q-btn-dropdown v-if="index || index == 0" color="primary" split @click="insertOrEditTravel">
        <template #label> {{ formLabel('saveTravel') }} </template>
        <q-list>
          <q-item clickable v-close-popup @click="promptRemoveTravel">
            <q-item-section>
              <q-item-label>{{ formLabel('delete') }}</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </q-btn-dropdown>
    </template>
  </q-card-actions>
  <ModalTravelMileage
    v-model="modalTravelMileage"
    :stop-travel="mileageTooLarge"
    @confirm-travel="isIncomplete ? save() : insertOrEdit()"
  />
  <ModalPromptDelete v-model="modalPromptDelete" @confirm-delete="removeTravel" />
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeMount,
  onBeforeUpdate,
  onMounted,
  onUnmounted,
  ref,
  Ref,
  inject,
  unref,
  shallowRef,
} from 'vue'
import { useFormatDate } from '../composables/formatDate'
import { useTimers } from '../store/timers'
import { storeToRefs } from 'pinia'
import { useStartEndRules } from '../composables/startEndRules'
import { useFormatTime } from '../composables/formatTime'
import { useOdometerDistance } from '../composables/odometerDistance'
import { useOdometerRules } from '../composables/odometerRules'
import { useDestinationRules } from '../composables/destinationRules'
import { useI18n } from 'vue-i18n'
import { Timer } from '../types/timers'
import { useMapLocationsTravel } from '../composables/mapLocationsTravel'
import { useStoreProfiles } from '../store/useStoreProfiles'
import { QSelect } from 'quasar'
import TypeAheadStores from './TypeAheadStores.vue'
import { isValidDate, toDate, stripTime } from '../utils'
import ModalPromptDelete from './ModalPromptDelete.vue'
import ModalTravelMileage from './ModalTravelMileage.vue'
import { useUserStore } from '../store/useUserStore'

export default defineComponent({
  components: { ModalPromptDelete, TypeAheadStores, ModalTravelMileage },
  emits: ['started', 'cancel', 'close', 'saved'],
  props: {
    insert: {
      type: Boolean,
      required: false,
      default: false,
    },
    edit: {
      type: Boolean,
      required: false,
      default: false,
    },
    insertTimes: {
      type: Object,
      required: false,
      default: undefined,
    },
    insertOdo: {
      type: Object,
      required: false,
      default: undefined,
    },
    index: {
      required: false,
      type: Number,
      default: undefined,
    },
    locations: {
      required: false,
      type: Object,
      default: undefined,
    },
  },
  setup(props, { emit }) {
    //translations
    const { t } = useI18n({ useScope: 'global' })
    const formLabel = (key: string) => t(`form.${key}`)
    const errorMessage = (key: string) => t(`form.messages.${key}`)
    const date = inject('date', ref(new Date()))

    const start = ref(new Date())
    stripTime(start, date.value)

    const startRef = ref()

    const startOdo = ref(props.insertOdo ? props.insertOdo.startOdo : 0)
    const startOdoRef = ref()
    const departing = ref()
    const destination = ref()

    //Data
    const locationOptions = ref([
      { name: 'home', type: 'home' },
      { name: 'warehouse', type: 'warehouse' },
      { name: 'other', type: 'other' },
    ])
    const { getStores } = storeToRefs(useStoreProfiles())
    const storeList = shallowRef(
      getStores.value.data.map((store) => {
        return {
          type: 'store',
          store_id: store.store_id,
          name: store.store_name,
          number: store.store_number,
          address: {
            street: store.street,
            city: store.city,
            state: store.state,
            zip: store.zip,
          },
        }
      })
    )
    const allLocationOptions = shallowRef([...locationOptions.value, ...storeList.value]) as Ref<
      {
        name: string
        type: 'home' | 'warehouse' | 'store' | 'other'
        number?: number
        store_id?: number
        address?: { street: string; city: string; state: string; zip: string }
      }[]
    >
    const locationInputs = ref([
      {
        name: 'departing',
        ref: departing,
        label: formLabel('departingFrom'),
      },
      {
        name: 'destination',
        ref: destination,
        label: formLabel('destination'),
      },
    ])
    const locationRefs = ref<QSelect[]>([])
    function getLocationRef(index: number) {
      function functionRef(element: QSelect) {
        locationRefs.value[index] = element
      }
      return functionRef
    }
    onBeforeUpdate(() => {
      locationRefs.value = []
    })
    const travelData = computed(() => {
      return {
        odometer_start: startOdo,
        odometer_end: endOdo,
        departing: departing.value.type,
        departingStoreId: departing.value.type === 'store' ? departing.value.store_id : null,
        departingStoreName: departing.value.type === 'store' ? departing.value.name : null,
        departingStoreNumber: departing.value.type === 'store' ? departing.value.number : null,
        destination: destination.value.type,
        destinationStoreId: destination.value.type === 'store' ? destination.value.store_id : null,
        destinationStoreName: destination.value.type === 'store' ? destination.value.name : null,
        destinationStoreNumber:
          destination.value.type === 'store' ? destination.value.number : null,
      }
    })

    //state trajectory
    const initContinue = ref(false)
    const { getActiveParentTimer, getAllParentTimers } = storeToRefs(useTimers())
    const {
      startDriving,
      endDriving,
      editParentData,
      removeParent,
      canEdit,
      timeOverlaps,
      timerConflict,
      sortTimers,
    } = useTimers()
    const isIncomplete = computed(() =>
      getAllParentTimers.value.length > 0
        ? getAllParentTimers.value[getAllParentTimers.value.length - 1].data.incomplete
        : false
    )
    const traveling = computed(() => {
      return getActiveParentTimer.value?.type === 'travel' ? true : false
    })
    const travel = () => {
      stripTime(start, date.value)
      startDriving({ date: start.value, odometer_start: startOdo })
      editParentData(getAllParentTimers.value.length - 1, {
        incomplete: true, // for restarting
        ...travelData.value,
      })
      emit('started')
    }
    const stopped = ref<boolean>(false)
    const end = ref()
    const endRef = ref()
    onBeforeMount(() => {
      //Set start and end Odo values for inserting between timers
      if (props.insert && props.insertTimes) {
        start.value = props.insertTimes.start
        end.value = props.insertTimes.end
        endOdo.value = startOdo.value + 0
      }
      //Handle inserting Odo readings for inserting between timers
      if (typeof props.insertOdo !== 'undefined') {
        startOdo.value = props.insertOdo.startOdo
        if (typeof props.insertOdo.endOdo === 'undefined') {
          endOdo.value = useOdometerDistance(
            startOdo,
            getAllParentTimers.value[getAllParentTimers.value.length - 1] as Timer
          ).endOdometerGuess.value
        } else {
          endOdo.value = props.insertOdo.endOdo
        }
      }
      //Handle default location settings if supplied ( between timers or editing )
      if (typeof props.locations !== 'undefined') {
        departing.value =
          props.locations!.departing !== 'store'
            ? findAllLocations(props.locations!.departing, 'name')
            : findAllLocations(props.locations!.departingStoreNumber, 'number')

        destination.value =
          props.locations!.destination !== 'store'
            ? findAllLocations(props.locations!.destination, 'name')
            : findAllLocations(props.locations!.destinationStoreNumber, 'number')
      } else {
        //Handle default location setting if none supplied (new travel activity)
        //Finds previous departing value and sets it
        if (getAllParentTimers.value.length > 0) {
          const lastLocation = useMapLocationsTravel().departing.value
          if (typeof lastLocation !== 'undefined') {
            departing.value =
              lastLocation.departing === 'store'
                ? findAllLocations(lastLocation.departingStoreNumber!, 'number')
                : findAllLocations(lastLocation.departing!, 'name')
          }
        }
      }
    })
    const stop = () => {
      endOdo.value = unref(startOdo.value)
      end.value = toDate(new Date(), date.value)
      stripTime(end, date.value)
      endDriving({ date: end.value })
      stopped.value = true
    }
    //listen for stopped travel event
    onMounted(() => {
      window.addEventListener('stoppedTravel', () => {
        resetForm()
        emit('cancel')
      })
    })
    onUnmounted(() => {
      window.removeEventListener('stoppedTravel', () => {
        resetForm()
        emit('cancel')
      })
    })
    const endOdo = ref()
    const endOdoRef = ref()
    const distance = computed(() => {
      if (endOdo.value < 0 || typeof endOdo.value === 'undefined') {
        return ''
      }
      return endOdo.value - startOdo.value
    })
    const duration = computed(() =>
      end.value && start.value
        ? useFormatTime(end.value.getTime() - start.value.getTime(), {
            format: 'hh:mm',
            initTime: 'ms',
          })
        : ''
    )

    //sanity check no rules were violated
    const valid = computed(() => {
      return rules.all(props.index) === true && odoRules.all() === true && destinationRules.all()
    })
    //Runs quasars validation
    const validate = () => {
      if (startRef.value) {
        startRef.value.validate()
      }
      if (startOdoRef.value) {
        startOdoRef.value.validate()
      }
      if (endRef.value) {
        endRef.value.validate()
      }
      if (endOdoRef.value) {
        endOdoRef.value.validate()
      }
      for (const location of locationRefs.value) {
        void location.validate() // not really async in this case
      }
    }
    const modalTravelMileage = ref(false)
    const mileageTooLarge = ref(false)
    const saveTravel = () => {
      validate()
      mileageTooLarge.value = false
      if (
        odoRules.greaterThan(useUserStore().distance == 'km' ? 640 : 400)(
          startOdo.value,
          endOdo.value
        )
      ) {
        modalTravelMileage.value = true
        mileageTooLarge.value = true
        console.log('over 400', mileageTooLarge.value)
        return void 0
      }
      //delay updating timer
      if (
        odoRules.greaterThan(useUserStore().distance == 'km' ? 160 : 100)(
          startOdo.value,
          endOdo.value
        )
      ) {
        modalTravelMileage.value = true
        return void 0
      }
      save()
    }
    const save = () => {
      if (valid.value) {
        const index = getAllParentTimers.value.length - 1
        editParentData(index, {
          incomplete: false, // for restarting
          ...travelData.value,
        })
        editParentStartEnd({ start_time: start.value, end_time: end.value }, index)

        sortTimers()
        resetForm()
        emit('saved')
      }
    }
    const { insertNewTimer, editParentStartEnd } = useTimers()
    const insertOrEdit = () => {
      if (valid.value) {
        const data = {
          data: {
            incomplete: false, // for restarting
            ...travelData.value,
          },
        }
        if (props.edit) {
          editParentStartEnd({ start_time: start.value, end_time: end.value }, props.index!)
          editParentData(props.index!, data.data)
        } else {
          void insertNewTimer({
            start_time: start.value,
            end_time: end.value,
            type: 'travel',
            ...data,
          })
        }

        sortTimers()
        emit('saved')
      }
    }
    const insertOrEditTravel = () => {
      validate()
      mileageTooLarge.value = false
      if (
        odoRules.greaterThan(useUserStore().distance == 'km' ? 640 : 400)(
          startOdo.value,
          endOdo.value
        )
      ) {
        modalTravelMileage.value = true
        mileageTooLarge.value = true
        return void 0
      }

      //delay updating timer
      if (
        odoRules.greaterThan(useUserStore().distance == 'km' ? 160 : 100)(
          startOdo.value,
          endOdo.value
        )
      ) {
        modalTravelMileage.value = true
        return void 0
      }
      insertOrEdit()
    }
    const close = () => {
      resetForm()
      emit('cancel')
    }
    const modalPromptDelete = ref(false)
    const promptRemoveTravel = () => {
      modalPromptDelete.value = true
    }
    const removeTravel = () => {
      removeParent(
        typeof props.index !== 'undefined' ? props.index : getAllParentTimers.value.length - 1
      )
      resetForm()
      emit('close')
    }
    const resetForm = () => {
      end.value = undefined
      initContinue.value = false
      stopped.value = false
    }

    //rules
    const rules = useStartEndRules({ start, end, index: props.index })
    const odoRules = useOdometerRules({ start: startOdo, end: endOdo })
    const destinationRules = useDestinationRules({
      departing: departing,
      destination: destination,
    })
    //utils
    const updateTimer =
      (timerRef: Ref<Date>) =>
      (newDate: Date): void => {
        console.log(`update ${timerRef.value}`, newDate)
        timerRef.value = newDate
      }
    const updateStart = updateTimer(start)
    const updateEnd = updateTimer(end)
    /** look up allLocationOptions by name or number property */
    const findAllLocations = (item: string | number | undefined, field: 'name' | 'number') =>
      allLocationOptions.value.find((location) => {
        if (field === 'name') return location.name == item
        else if (field === 'number') {
          return location.number == item
        } else return undefined
      })
    const options = ref([...allLocationOptions.value])

    return {
      //translation
      formLabel,
      errorMessage,

      //initial state
      start,
      startOdo,
      departing,
      // locationOptions,
      options,
      allLocationOptions,
      destination,
      locationInputs,

      //template refs
      startRef,
      startOdoRef,
      endRef,
      endOdoRef,
      getLocationRef,
      // storeList,

      //start travel states
      initContinue,
      isIncomplete,
      traveling,
      travel,
      stop,
      stopped,

      //end state data
      end,
      endOdo,
      distance,
      duration,

      //save, insert, edit, remove travel
      saveTravel,
      save,
      insertOrEditTravel,
      insertOrEdit,
      removeTravel,
      close,

      //rules
      rules,
      odoRules,
      destinationRules,
      isValidDate,
      //utils
      toDate,
      updateStart,
      updateEnd,
      getActiveParentTimer,
      canEdit,
      timeOverlaps,
      timerConflict,
      useFormatDate,

      //remove
      modalPromptDelete,
      promptRemoveTravel,

      modalTravelMileage,
      mileageTooLarge,
      date,
    }
  },
})
</script>
